import { FieldSet } from "@prismicio/editor-ui"
import {
  GroupContent,
  isGroupContent,
  isNestableContent,
  isUIDContent,
  NestableContent,
  UIDContent
} from "@prismicio/types-internal/lib/content"
import { useMemo } from "react"

import { UIDField } from "../fields/UIDField"
import { GroupEditor } from "../GroupEditor"
import { NestableWidgetSwitch } from "../NestableWidgetSwitch"
import type { StaticZone, StaticZoneContent } from "./StaticZone"

interface StaticZoneEditorProps {
  id: string
  readOnly: boolean
  staticZone: StaticZone
  content: StaticZoneContent
  onContentChange: (content: StaticZoneContent) => void
}

export function StaticZoneEditor(props: StaticZoneEditorProps) {
  const { id, readOnly, staticZone, content, onContentChange } = props
  const staticWidgets = useMemo(() => Object.entries(staticZone), [staticZone])

  if (staticWidgets.length === 0) return null

  return (
    <FieldSet legend="Page data">
      {staticWidgets.map(([staticWidgetId, staticWidget]) => {
        const staticWidgetProps = {
          id: `${id}.${staticWidgetId}`,
          onContentChange: (
            staticWidgetContent:
              | UIDContent
              | GroupContent
              | NestableContent
              | undefined
          ) => {
            const { [staticWidgetId]: _, ...prevContent } = content
            onContentChange({
              ...prevContent,
              ...(staticWidgetContent !== undefined && {
                [staticWidgetId]: staticWidgetContent
              })
            })
          },
          readOnly
        }
        const staticWidgetContent = content[staticWidgetId]

        if (staticWidget.type === "UID") {
          return (
            <UIDField
              {...staticWidgetProps}
              content={
                isUIDContent(staticWidgetContent)
                  ? staticWidgetContent
                  : undefined
              }
              key={staticWidgetId}
              field={staticWidget}
            />
          )
        } else if (staticWidget.type === "Group") {
          return (
            <GroupEditor
              {...staticWidgetProps}
              content={
                isGroupContent(staticWidgetContent)
                  ? staticWidgetContent
                  : undefined
              }
              group={staticWidget}
              key={staticWidgetId}
            />
          )
        } else {
          return (
            <NestableWidgetSwitch
              {...staticWidgetProps}
              content={
                isNestableContent(staticWidgetContent)
                  ? staticWidgetContent
                  : undefined
              }
              key={staticWidgetId}
              nestableWidget={staticWidget}
            />
          )
        }
      })}
    </FieldSet>
  )
}
